import styled from '@emotion/styled';
import { Button } from '@material-ui/core';
import { X } from 'lucide-react';
import React from 'react';
import { ETItem, KIND_LABEL } from '../../../../components/ExperienceTimeline';

export const INSPECTOR_RAIL_WIDTH = 300;

const Panel = styled('div')`
  width: ${INSPECTOR_RAIL_WIDTH}px;
  flex-shrink: 0;
  background: ${(p) => p.theme.custom.colors.gray.white};
  border-right: 1px solid ${(p) => p.theme.custom.colors.gray.w200};
  padding: ${(p) => p.theme.spacing(2.5)}px;
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing(1.5)}px;
`;

const HeaderRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: ${(p) => p.theme.spacing(1)}px;
`;

const Title = styled('span')`
  font-size: 16px;
  font-weight: 600;
  color: ${(p) => p.theme.custom.colors.gray.w900};
  flex: 1;
`;

const CloseButton = styled('div')`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid ${(p) => p.theme.custom.colors.gray.w300};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${(p) => p.theme.custom.colors.gray.w500};
  flex-shrink: 0;
`;

const Eyebrow = styled('span')<{ color: string }>`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(p) => p.color};
`;

const InfoRow = styled('div')`
  display: flex;
  gap: ${(p) => p.theme.spacing(2.5)}px;
  font-size: 12px;
  color: ${(p) => p.theme.custom.colors.gray.w900};
  padding-top: ${(p) => p.theme.spacing(0.5)}px;
  border-top: 1px solid ${(p) => p.theme.custom.colors.gray.w200};
`;

const InfoLabel = styled('div')`
  color: ${(p) => p.theme.custom.colors.gray.w500};
  font-weight: 300;
`;

const ReasonBox = styled('div')`
  font-size: 12px;
  color: ${(p) => p.theme.custom.colors.gray.w500};
  font-weight: 300;
  background: ${(p) => p.theme.custom.colors.gray.w100};
  border-radius: 6px;
  padding: ${(p) => p.theme.spacing()}px;
`;

const LinkLine = styled('div')`
  font-size: 12px;
  color: ${(p) => p.theme.custom.colors.gray.w500};
  font-weight: 300;
`;

const ActionsRow = styled('div')`
  display: flex;
  gap: ${(p) => p.theme.spacing()}px;
  margin-top: auto;
`;

const whenLabel = (item: ETItem) => {
  const prefix = item.approximate ? '~' : '';
  if (item.kind === 'TASK' && item.startDay !== item.endDay) {
    return `${prefix}Day ${item.startDay} – ${item.endDay}`;
  }
  return `${prefix}Day ${item.startDay}`;
};

export const TimelineInspectorRail = ({
  item,
  actorLabel,
  triggeredByLabel,
  triggersCount,
  onClose,
  onView,
  onEdit,
  canEdit = true
}: {
  item: ETItem;
  actorLabel: string;
  triggeredByLabel?: string | null;
  triggersCount?: number;
  onClose: () => void;
  onView?: () => void;
  onEdit?: () => void;
  canEdit?: boolean;
}) => (
  <Panel>
    <HeaderRow>
      <Title>{item.label}</Title>
      <CloseButton onClick={onClose}>
        <X size={14} />
      </CloseButton>
    </HeaderRow>
    <Eyebrow color={item.color}>{KIND_LABEL[item.kind]}</Eyebrow>
    <InfoRow>
      <div>
        <InfoLabel>Actor</InfoLabel>
        {actorLabel}
      </div>
      <div>
        <InfoLabel>Timing</InfoLabel>
        {whenLabel(item)}
      </div>
    </InfoRow>
    {triggeredByLabel && <LinkLine>Triggered by {triggeredByLabel}</LinkLine>}
    {!!triggersCount && (
      <LinkLine>
        Triggers {triggersCount} item{triggersCount > 1 ? 's' : ''}
      </LinkLine>
    )}
    {item.variableStart && <ReasonBox>{item.variableStart.reason}</ReasonBox>}
    <ActionsRow>
      {onView && (
        <Button variant="outlined" color="default" size="small" onClick={onView} style={{ flex: 1 }}>
          View
        </Button>
      )}
      {canEdit && onEdit && (
        <Button variant="contained" color="primary" size="small" onClick={onEdit} style={{ flex: 1 }}>
          Edit
        </Button>
      )}
    </ActionsRow>
  </Panel>
);
